import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BestBoulderCard, CommentBouldersSector, GymMinimalProps, GymStats, SectorsOverview } from '../constants/types';
import { getGymGeneralStats, getLatestGymRouteComments, getSectorsOverview, getTopBoulders } from '../data/getAxiosRequests';

interface UseHomePageDataReturn {
    gymStats: GymStats | undefined;
    bestBoulders: BestBoulderCard[] | undefined;
    comments: CommentBouldersSector[];
    sectorsOverview: SectorsOverview[];
    latestOpeningSector: { id: number; name: string; relevantDate: string } | undefined;
    upcomingClosingSectors: { id: number; name: string; relevantDate: string }[];
    zeroBouldersSectors: { id: number; name: string; relevantDate: string }[];
    isLoading: boolean;
}

export function useHomePageData(
    user: string | null,
    currentGym: GymMinimalProps | null,
): UseHomePageDataReturn {
    const navigate = useNavigate();

    const [gymStats, setGymStats] = useState<GymStats | undefined>(undefined);
    const [bestBoulders, setBestBoulders] = useState<BestBoulderCard[] | undefined>(undefined);
    const [comments, setComments] = useState<CommentBouldersSector[]>([]);
    const [sectorsOverview, setSectorsOverview] = useState<SectorsOverview[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (user && currentGym) {
            setIsLoading(true);
            getGymGeneralStats(user, currentGym.id).then(data => {
                setGymStats(data);
            });
            getTopBoulders(currentGym.id).then(data => {
                setBestBoulders(data);
            });
            getLatestGymRouteComments(currentGym.id).then(data => {
                setComments(data);
            });
            getSectorsOverview(user, currentGym.id).then(data => {
                const sortedData = data.sort((a: SectorsOverview, b: SectorsOverview) => {
                    const dateA = new Date(a.plannedClosingDate).getTime();
                    const dateB = new Date(b.plannedClosingDate).getTime();
                    return dateA - dateB;
                });
                setSectorsOverview(sortedData);
                setIsLoading(false);
            });
        } else {
            if (!localStorage.getItem('user')) {
                navigate('/login-pro');
            }
            console.error("user not provided");
            setIsLoading(false);
        }
    }, [user, currentGym]);

    const latestOpeningSector = useMemo(() => {
        if (sectorsOverview.length === 0) return undefined;
        return sectorsOverview.reduce((latest, sector) => {
            return !latest || new Date(sector.openingDate) > new Date(latest.relevantDate)
                ? { id: sector.id, name: sector.name, relevantDate: sector.openingDate }
                : latest;
        }, undefined as { id: number; name: string; relevantDate: string } | undefined);
    }, [sectorsOverview]);

    const upcomingClosingSectors = useMemo(() => {
        const today = new Date();
        return sectorsOverview
            .filter(sector => new Date(sector.plannedClosingDate) < today)
            .map(sector => ({
                id: sector.id,
                name: sector.name,
                relevantDate: sector.plannedClosingDate,
            }));
    }, [sectorsOverview]);

    const zeroBouldersSectors = useMemo(() => {
        return sectorsOverview
            .filter(sector => sector.countBoulders === 0)
            .map(sector => ({
                id: sector.id,
                name: sector.name,
                relevantDate: sector.plannedClosingDate,
            }));
    }, [sectorsOverview]);

    return {
        gymStats,
        bestBoulders,
        comments,
        sectorsOverview,
        latestOpeningSector,
        upcomingClosingSectors,
        zeroBouldersSectors,
        isLoading,
    };
}
